import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { AthleteThreshold } from '../types/database.tables'
import type { CalculatedZone } from '../types/zones'

interface ThresholdForDate {
  vt1Bpm: number | null
  vt2Bpm: number | null
  maxHrBpm: number | null
  hrZones?: CalculatedZone[]
  paceZones?: CalculatedZone[]
  powerZones?: CalculatedZone[]
  effectiveDate: string | null
  source: string | null
  activityType: string | null
}

interface UseAthleteThresholdsReturn {
  thresholds: AthleteThreshold[]
  currentThresholds: ThresholdForDate | null
  loading: boolean
  error: string | null
  getThresholdsForDate: (activityType: string, date: string) => Promise<ThresholdForDate | null>
  addThreshold: (threshold: Omit<AthleteThreshold, 'id' | 'created_at' | 'updated_at'>) => Promise<void>
  updateThreshold: (id: string, threshold: Partial<AthleteThreshold>) => Promise<void>
  deleteThreshold: (id: string) => Promise<void>
  refreshThresholds: () => Promise<void>
}

export const useAthleteThresholds = (athleteId: string): UseAthleteThresholdsReturn => {
  const [thresholds, setThresholds] = useState<AthleteThreshold[]>([])
  const [currentThresholds, setCurrentThresholds] = useState<ThresholdForDate | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadThresholds = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: supabaseError } = await supabase
        .from('athlete_thresholds')
        .select('*')
        .eq('athlete_id', athleteId)
        .order('effective_date', { ascending: false })

      if (supabaseError) {
        throw new Error(supabaseError.message)
      }

      setThresholds(data || [])

      // Set current thresholds (most recent across all activity types)
      if (data && data.length > 0) {
        const latest = data[0]
        setCurrentThresholds({
          vt1Bpm: latest.vt1_bpm,
          vt2Bpm: latest.vt2_bpm,
          maxHrBpm: latest.max_hr_bpm,
          effectiveDate: latest.effective_date,
          source: latest.source,
          activityType: latest.activity_type,
        })
      } else {
        setCurrentThresholds(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load athlete thresholds')
      console.error('Error loading athlete thresholds:', err)
    } finally {
      setLoading(false)
    }
  }, [athleteId])

  const getThresholdsForDate = useCallback(
    async (activityType: string, date: string): Promise<ThresholdForDate | null> => {
      try {
        const { data, error: supabaseError } = await supabase.rpc('get_athlete_thresholds_for_date', {
          p_athlete_id: athleteId,
          p_activity_type: activityType,
          p_date: date,
        })

        if (supabaseError) {
          throw new Error(supabaseError.message)
        }

        if (!data || data.length === 0) {
          return null
        }

        const result = data[0]

        // Calculate zones from zone configs if available
        let hrZones: CalculatedZone[] | undefined
        let paceZones: CalculatedZone[] | undefined
        let powerZones: CalculatedZone[] | undefined

        if (result.hr_zone_config) {
          hrZones = result.hr_zone_config as unknown as CalculatedZone[]
        }

        if (result.pace_zone_config) {
          paceZones = result.pace_zone_config as unknown as CalculatedZone[]
        }

        if (result.power_zone_config) {
          powerZones = result.power_zone_config as unknown as CalculatedZone[]
        }

        return {
          vt1Bpm: result.vt1_bpm,
          vt2Bpm: result.vt2_bpm,
          maxHrBpm: result.max_hr_bpm,
          hrZones,
          paceZones,
          powerZones,
          effectiveDate: result.effective_date,
          source: result.source,
          activityType: result.activity_type,
        }
      } catch (err) {
        console.error('Error getting thresholds for date:', err)
        return null
      }
    },
    [athleteId]
  )

  const addThreshold = useCallback(
    async (thresholdData: Omit<AthleteThreshold, 'id' | 'created_at' | 'updated_at'>) => {
      try {
        setError(null)

        const { error: supabaseError } = await supabase.from('athlete_thresholds').insert(thresholdData)

        if (supabaseError) {
          throw new Error(supabaseError.message)
        }

        await loadThresholds()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to add threshold'
        setError(errorMessage)
        console.error('Error adding threshold:', err)
        throw new Error(errorMessage)
      }
    },
    [loadThresholds]
  )

  const updateThreshold = useCallback(
    async (id: string, thresholdData: Partial<AthleteThreshold>) => {
      try {
        setError(null)
        const { error: supabaseError } = await supabase.from('athlete_thresholds').update(thresholdData).eq('id', id)
        if (supabaseError) {
          throw new Error(supabaseError.message)
        }
        await loadThresholds()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update threshold'
        setError(errorMessage)
        console.error('Error updating threshold:', err)
        throw new Error(errorMessage)
      }
    },
    [loadThresholds]
  )

  const deleteThreshold = useCallback(
    async (id: string) => {
      try {
        setError(null)

        const { error: supabaseError } = await supabase.from('athlete_thresholds').delete().eq('id', id)

        if (supabaseError) {
          throw new Error(supabaseError.message)
        }

        await loadThresholds()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete threshold'
        setError(errorMessage)
        console.error('Error deleting threshold:', err)
        throw new Error(errorMessage)
      }
    },
    [loadThresholds]
  )

  const refreshThresholds = useCallback(async () => {
    await loadThresholds()
  }, [loadThresholds])

  useEffect(() => {
    if (athleteId) {
      loadThresholds()
    }
  }, [athleteId, loadThresholds])

  return {
    thresholds,
    currentThresholds,
    loading,
    error,
    getThresholdsForDate,
    addThreshold,
    updateThreshold,
    deleteThreshold,
    refreshThresholds,
  }
}
